import {
  type AiStatus,
  ALLOWED_COST_DOCUMENT_TYPES,
  ApiError,
  formatMoney,
  type InvoiceExtraction,
  isUnitTracked,
  MAX_COST_DOCUMENT_BYTES,
  type ProductType,
  queryKeys,
  VENDOR_PAYMENT_STATUSES,
  type VendorPaymentStatus,
} from '@mevaser/shared'
import {
  Badge,
  type BadgeProps,
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyState,
  Field,
  humanizeStatus,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
  Textarea,
  useToast,
} from '@mevaser/ui'
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import type { ColumnDef } from '@tanstack/react-table'
import { ClipboardList, FileText, Paperclip, Plus, Trash2 } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Page, PageHeader } from '../components/app-shell.js'
import { DataTable } from '../components/data-table.js'
import { usePaymentLookups } from '../components/record-payment-dialog.js'
import { UnpaidOrderWarning } from '../components/unpaid-order-warning.js'
import { api } from '../lib/api.js'
import { useAuth } from '../lib/auth.js'

export const PURCHASE_ORDER_STATUS_TONE: Record<string, NonNullable<BadgeProps['tone']>> = {
  ordered: 'warning',
  partially_received: 'info',
  received: 'success',
  closed: 'neutral',
  cancelled: 'neutral',
}

export const ALLOWED_DOCUMENT_TYPE_LIST = ALLOWED_COST_DOCUMENT_TYPES.join(',')

export function validateInvoiceFile(file: File, toast: ReturnType<typeof useToast>): boolean {
  if (!(ALLOWED_COST_DOCUMENT_TYPES as readonly string[]).includes(file.type)) {
    toast.error('Could not attach', 'Invoices must be a PDF, JPEG, PNG or WebP image.')
    return false
  }
  if (file.size > MAX_COST_DOCUMENT_BYTES) {
    toast.error('Could not attach', `${file.name} is larger than 10 MB.`)
    return false
  }
  return true
}

export interface PurchaseOrderRow {
  id: string
  poNumber: string
  status: string
  vendor: string
  invoiceRef: string | null
  expectedLocationId: string | null
  expectedLocationName: string | null
  note: string | null
  itemCount: number
  orderedQty: number
  pendingQty: number
  hasDocument: boolean
  createdAt: string
}

interface Location {
  id: string
  name: string
  isActive: boolean
}

interface ProductRow {
  id: string
  sku: string
  name: string
  type: ProductType
}

type StatusFilter =
  | 'open'
  | 'all'
  | 'ordered'
  | 'partially_received'
  | 'received'
  | 'closed'
  | 'cancelled'

/**
 * Vendor orders and invoices. What is on an order is pending — on order, not
 * stock — until it is marked received on the order's page.
 */
export function PurchaseOrdersPage() {
  const { can } = useAuth()
  const navigate = useNavigate()
  const [status, setStatus] = useState<StatusFilter>('open')
  const [q, setQ] = useState('')
  const [creating, setCreating] = useState(false)

  const filters = {
    pageSize: 50,
    status: status === 'all' ? undefined : status,
    q: q.trim() || undefined,
  }

  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery({
    queryKey: queryKeys.purchaseOrders.list(filters),
    queryFn: ({ pageParam }) =>
      api.get<{ items: PurchaseOrderRow[]; total: number; page: number; pageSize: number }>(
        '/purchase-orders',
        { query: { ...filters, page: pageParam } },
      ),
    initialPageParam: 1,
    getNextPageParam: (last) =>
      last.page * last.pageSize < last.total ? last.page + 1 : undefined,
  })
  const rows = data?.pages.flatMap((p) => p.items) ?? []
  const total = data?.pages[0]?.total ?? 0

  const columns = useMemo<ColumnDef<PurchaseOrderRow, unknown>[]>(
    () => [
      {
        header: 'Order',
        accessorKey: 'poNumber',
        cell: ({ row }) => <span className="font-identifier">{row.original.poNumber}</span>,
      },
      { header: 'Vendor', accessorKey: 'vendor' },
      {
        header: 'Invoice',
        accessorKey: 'invoiceRef',
        cell: ({ row }) => (
          <span className="inline-flex items-center gap-1">
            {row.original.invoiceRef ?? '—'}
            {row.original.hasDocument ? (
              <Paperclip className="size-3 text-foreground-muted" aria-label="Invoice attached" />
            ) : null}
          </span>
        ),
      },
      {
        header: 'Status',
        accessorKey: 'status',
        cell: ({ row }) => (
          <Badge tone={PURCHASE_ORDER_STATUS_TONE[row.original.status] ?? 'neutral'}>
            {humanizeStatus(row.original.status)}
          </Badge>
        ),
      },
      {
        header: 'Pending',
        accessorKey: 'pendingQty',
        cell: ({ row }) => (
          <span className="tabular">
            {row.original.pendingQty} of {row.original.orderedQty}
          </span>
        ),
      },
      {
        header: 'Entered',
        accessorKey: 'createdAt',
        cell: ({ row }) =>
          new Date(row.original.createdAt).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
          }),
      },
    ],
    [],
  )

  return (
    <Page>
      <PageHeader
        title="Purchase orders"
        description="Enter a vendor order or invoice. Its items stay pending — on order, not in stock — until you mark them received."
        actions={
          can('inventory:write') ? (
            <Button variant="primary" onClick={() => setCreating(true)}>
              <Plus className="size-4" aria-hidden="true" /> New purchase order
            </Button>
          ) : null
        }
      />

      <div className="mb-4 flex flex-wrap items-end gap-3">
        <Field label="Status">
          {(props) => (
            <Select value={status} onValueChange={(v) => setStatus(v as StatusFilter)}>
              <SelectTrigger {...props} className="w-44">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="open">Open (waiting on goods)</SelectItem>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="ordered">Ordered</SelectItem>
                <SelectItem value="partially_received">Partially received</SelectItem>
                <SelectItem value="received">Received</SelectItem>
                <SelectItem value="closed">Closed</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
          )}
        </Field>
        <Field label="Search">
          {(props) => (
            <Input
              {...props}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="PO number, vendor, invoice…"
            />
          )}
        </Field>
      </div>

      <DataTable
        columns={columns}
        data={rows}
        isLoading={isLoading}
        onRowClick={(row) =>
          void navigate({ to: '/purchase-orders/$orderId', params: { orderId: row.id } })
        }
        infinite={{
          total,
          hasMore: Boolean(hasNextPage),
          isFetchingMore: isFetchingNextPage,
          onLoadMore: () => void fetchNextPage(),
        }}
        empty={
          <EmptyState
            icon={ClipboardList}
            title="No purchase orders"
            description="Enter an order or invoice to track what is on its way before it reaches the shelf."
          />
        }
      />

      <NewPurchaseOrderDialog
        open={creating}
        onClose={() => setCreating(false)}
        onCreated={(id, recordPayment) =>
          void navigate({
            to: '/purchase-orders/$orderId',
            params: { orderId: id },
            ...(recordPayment ? { search: { recordPayment: true } } : {}),
          })
        }
      />
    </Page>
  )
}

/** Dollars typed in a field → cents; blank is "not entered". */
function toCents(value: string): number | null {
  return value.trim() === '' ? null : Math.round(Number(value) * 100)
}

interface DraftLine {
  key: string
  /** Empty while an invoice line is not yet matched to a catalogue product. */
  productId: string
  name: string
  sku: string
  qty: string
  cost: string
  /** The wording on the invoice, when the line came from reading one. */
  invoiceText?: string
  /** t1223: an office-supplies line — saved as an expense, not as stock. `name` is its description. */
  office?: boolean
}

function NewPurchaseOrderDialog({
  open,
  onClose,
  onCreated,
}: {
  open: boolean
  onClose: () => void
  /** `recordPayment` is set when the user chose to record the payment straight after saving. */
  onCreated: (id: string, recordPayment: boolean) => void
}) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [vendor, setVendor] = useState('')
  const [invoiceRef, setInvoiceRef] = useState('')
  const [locationId, setLocationId] = useState('')
  const [note, setNote] = useState('')
  const [search, setSearch] = useState('')
  const [lines, setLines] = useState<DraftLine[]>([])
  const [file, setFile] = useState<File | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const readInputRef = useRef<HTMLInputElement>(null)
  const { can } = useAuth()
  const [invoiceTotalCents, setInvoiceTotalCents] = useState<number | null>(null)
  const [shipping, setShipping] = useState('')
  const [tax, setTax] = useState('')
  const [paymentStatus, setPaymentStatus] = useState<VendorPaymentStatus>('unpaid')
  const [allocateShipping, setAllocateShipping] = useState(false)
  const [allocateTax, setAllocateTax] = useState(false)
  const [fixingKey, setFixingKey] = useState<string | null>(null)
  const [expenseCategoryId, setExpenseCategoryId] = useState('')

  const { data: aiStatus } = useQuery({
    queryKey: queryKeys.ai.status(),
    queryFn: () => api.get<AiStatus>('/ai/status'),
    enabled: open && can('ai:assist'),
    staleTime: 60_000,
  })
  const canReadInvoice = can('ai:assist') && Boolean(aiStatus?.text)

  const { data: locations } = useQuery({
    queryKey: queryKeys.locations(),
    queryFn: () => api.get<Location[]>('/locations'),
    enabled: open,
  })
  const activeLocations = (locations ?? []).filter((l) => l.isActive)

  // t1223: office supplies on the same invoice become an expense, which needs expense access.
  const canExpense = can('expenses:manage')
  const { categories } = usePaymentLookups(open && canExpense)
  const hasOfficeLines = lines.some((l) => l.office)
  useEffect(() => {
    if (!hasOfficeLines || expenseCategoryId) return
    const preferred = categories.find((c) => c.name.trim().toLowerCase() === 'office supplies')
    if (preferred) setExpenseCategoryId(preferred.id)
  }, [hasOfficeLines, expenseCategoryId, categories])

  const { data: vendors } = useQuery({
    queryKey: queryKeys.vendors.list({ isActive: true, pageSize: 200 }),
    queryFn: () =>
      api.get<{ items: { id: string; name: string }[] }>('/vendors', {
        query: { isActive: true, pageSize: 200 },
      }),
    enabled: open,
  })

  const { data: products } = useQuery({
    queryKey: queryKeys.products.list({ q: search, forPurchaseOrder: true }),
    queryFn: () =>
      api.get<{ items: ProductRow[] }>('/products', {
        query: { q: search || undefined, isActive: true, pageSize: 20, sort: 'name', order: 'asc' },
      }),
    enabled: open && search.trim() !== '',
  })
  // Plans and services carry no stock — nothing to order.
  const matches = (products?.items ?? []).filter((p) => p.type === 'stock' || isUnitTracked(p.type))

  const readInvoice = useMutation({
    mutationFn: (picked: File) => {
      const formData = new FormData()
      formData.append('file', picked)
      return api.postFile<InvoiceExtraction>('/purchase-orders/read-invoice', formData)
    },
    onSuccess: (result, picked) => {
      setFile(picked)
      if (result.vendor) setVendor((current) => current || (result.vendor ?? ''))
      if (result.invoiceRef) setInvoiceRef((current) => current || (result.invoiceRef ?? ''))
      setInvoiceTotalCents(result.invoiceTotalCents)
      // Shipping and tax get their own lines; like vendor, only fill what is blank.
      const { shippingCents, taxCents } = result
      if (shippingCents !== null)
        setShipping((current) => current || (shippingCents / 100).toFixed(2))
      if (taxCents !== null) setTax((current) => current || (taxCents / 100).toFixed(2))
      // Appended in the order the invoice prints them; the admin reviews every
      // line below before anything is saved.
      setLines((current) => [
        ...current,
        ...result.lines.map((l) => ({
          key: crypto.randomUUID(),
          productId: l.match?.productId ?? '',
          name: l.match?.name ?? l.description,
          sku: l.match?.sku ?? l.sku ?? '',
          qty: String(l.qty),
          cost: l.unitCostCents === null ? '' : (l.unitCostCents / 100).toFixed(2),
          invoiceText: l.description,
        })),
      ])
      toast.success(
        `Read ${result.lines.length} item${result.lines.length === 1 ? '' : 's'}`,
        'Check each line before saving — nothing is ordered until you save.',
      )
    },
    onError: (error) =>
      toast.error(
        'Could not read that invoice',
        error instanceof ApiError ? error.message : 'Add the items by hand instead.',
      ),
  })

  const reset = () => {
    setInvoiceTotalCents(null)
    setShipping('')
    setTax('')
    setPaymentStatus('unpaid')
    setAllocateShipping(false)
    setAllocateTax(false)
    setFixingKey(null)
    setExpenseCategoryId('')
    setVendor('')
    setInvoiceRef('')
    setLocationId('')
    setNote('')
    setSearch('')
    setLines([])
    setFile(null)
  }

  const create = useMutation({
    mutationFn: (_recordPayment: boolean) =>
      api.post<{ id: string; poNumber: string; expensePaymentNumber: string | null }>('/purchase-orders', {
        vendor: vendor.trim(),
        invoiceRef: invoiceRef.trim() || null,
        expectedLocationId: locationId || null,
        note: note.trim() || null,
        shippingCents: toCents(shipping),
        taxCents: toCents(tax),
        paymentStatus,
        allocateShipping,
        allocateTax,
        items: lines
          .filter((l) => !l.office)
          .map((l) => ({
            productId: l.productId,
            qty: Number.parseInt(l.qty, 10),
            unitCostCents: l.cost.trim() === '' ? null : Math.round(Number(l.cost) * 100),
            note: null,
          })),
        expenseLines: lines
          .filter((l) => l.office)
          .map((l) => ({
            description: l.name.trim(),
            qty: Number.parseInt(l.qty, 10),
            unitCostCents: Math.round(Number(l.cost) * 100),
          })),
        expenseCategoryId: hasOfficeLines ? expenseCategoryId : null,
      }),
    onSuccess: async (created, recordPayment) => {
      if (file) {
        try {
          const formData = new FormData()
          formData.append('file', file)
          await api.postFile(`/purchase-orders/${created.id}/document`, formData)
        } catch (error) {
          toast.error(
            'Order saved, but the invoice file could not be attached',
            error instanceof ApiError ? error.message : 'Open the order and attach it from there.',
          )
        }
      }
      await queryClient.invalidateQueries({ queryKey: queryKeys.purchaseOrders.all() })
      toast.success(
        `${created.poNumber} entered`,
        created.expensePaymentNumber
          ? `Its items are pending until received. Office supplies saved as ${created.expensePaymentNumber}.`
          : 'Its items are pending until received.',
      )
      reset()
      onClose()
      onCreated(created.id, recordPayment)
    },
    onError: (error) =>
      toast.error(
        'Could not save the order',
        error instanceof ApiError ? error.message : undefined,
      ),
  })

  const goodsLines = lines.filter((l) => !l.office)
  const officeLines = lines.filter((l) => l.office)
  const linesValid =
    goodsLines.length > 0 &&
    goodsLines.every(
      (l) =>
        l.productId !== '' &&
        Number.parseInt(l.qty, 10) > 0 &&
        (l.cost === '' || Number(l.cost) >= 0),
    ) &&
    officeLines.every(
      (l) =>
        l.name.trim() !== '' && Number.parseInt(l.qty, 10) > 0 && Number(l.cost || 0) > 0,
    ) &&
    (officeLines.length === 0 || (canExpense && expenseCategoryId !== ''))
  const unmatched = goodsLines.some((l) => l.productId === '')
  const lineTotal = (l: DraftLine) =>
    (Number.parseInt(l.qty, 10) || 0) * Math.round(Number(l.cost || 0) * 100)
  const linesTotalCents = goodsLines.reduce((sum, l) => sum + lineTotal(l), 0)
  const officeTotalCents = officeLines.reduce((sum, l) => sum + lineTotal(l), 0)
  const extrasValid = [shipping, tax].every((v) => v.trim() === '' || Number(v) >= 0)
  const orderTotalCents = linesTotalCents + (toCents(shipping) ?? 0) + (toCents(tax) ?? 0)
  const valid = vendor.trim() !== '' && linesValid && extrasValid
  // A new order has no payments yet, so Paid here always leaves the whole total owed (t1201).
  const owesOnPaid = paymentStatus === 'paid' && valid && orderTotalCents > 0

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>New purchase order</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Vendor">
              {(props) => (
                <>
                  <Input
                    {...props}
                    list="po-vendor-options"
                    value={vendor}
                    onChange={(e) => setVendor(e.target.value)}
                  />
                  <datalist id="po-vendor-options">
                    {(vendors?.items ?? []).map((v) => (
                      <option key={v.id} value={v.name} />
                    ))}
                  </datalist>
                </>
              )}
            </Field>
            <Field label="Invoice / PO reference">
              {(props) => (
                <Input
                  {...props}
                  value={invoiceRef}
                  onChange={(e) => setInvoiceRef(e.target.value)}
                />
              )}
            </Field>
            <Field label="Arriving at" hint="Optional. Pre-fills the location when you receive it.">
              {(props) => (
                <Select
                  value={locationId || 'none'}
                  onValueChange={(v) => setLocationId(v === 'none' ? '' : v)}
                >
                  <SelectTrigger {...props}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Decide when received</SelectItem>
                    {activeLocations.map((l) => (
                      <SelectItem key={l.id} value={l.id}>
                        {l.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </Field>
            <Field label="Invoice file" hint="Optional. PDF, JPEG, PNG or WebP, up to 10 MB.">
              {() => (
                <div className="flex items-center gap-2">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept={ALLOWED_DOCUMENT_TYPE_LIST}
                    className="hidden"
                    onChange={(e) => {
                      const picked = e.target.files?.[0] ?? null
                      if (picked && validateInvoiceFile(picked, toast)) setFile(picked)
                      e.target.value = ''
                    }}
                  />
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <Paperclip className="size-4" /> {file ? 'Replace file' : 'Choose file'}
                  </Button>
                  {file ? (
                    <span className="truncate text-xs text-foreground-muted">{file.name}</span>
                  ) : null}
                </div>
              )}
            </Field>
          </div>

          {canReadInvoice ? (
            <div className="flex flex-wrap items-center gap-2 rounded-md border border-dashed border-border p-3">
              <input
                ref={readInputRef}
                type="file"
                accept={ALLOWED_DOCUMENT_TYPE_LIST}
                className="hidden"
                onChange={(e) => {
                  const picked = e.target.files?.[0] ?? null
                  if (picked && validateInvoiceFile(picked, toast)) readInvoice.mutate(picked)
                  e.target.value = ''
                }}
              />
              <Button
                type="button"
                variant="secondary"
                size="sm"
                loading={readInvoice.isPending}
                onClick={() => readInputRef.current?.click()}
              >
                <FileText className="size-4" />{' '}
                {readInvoice.isPending ? 'Reading invoice…' : 'Read items from invoice'}
              </Button>
              <span className="text-xs text-foreground-muted">
                Upload an invoice and its items are added below, in invoice order, for you to check.
              </span>
            </div>
          ) : null}

          <div>
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search the catalogue to add an item…"
              aria-label="Add an item"
            />
            {search.trim() ? (
              <ul className="mt-1 max-h-40 divide-y divide-border overflow-y-auto rounded-md border border-border">
                {matches.map((product) => (
                  <li key={product.id}>
                    <button
                      type="button"
                      className="w-full px-3 py-2 text-start text-sm hover:bg-surface-sunken"
                      onClick={() => {
                        if (fixingKey) {
                          setLines((current) =>
                            current.map((l) =>
                              l.key === fixingKey
                                ? {
                                    ...l,
                                    productId: product.id,
                                    name: product.name,
                                    sku: product.sku,
                                  }
                                : l,
                            ),
                          )
                          setFixingKey(null)
                          setSearch('')
                          return
                        }
                        setLines((current) =>
                          current.some((l) => l.productId === product.id)
                            ? current
                            : [
                                ...current,
                                {
                                  key: crypto.randomUUID(),
                                  productId: product.id,
                                  name: product.name,
                                  sku: product.sku,
                                  qty: '1',
                                  cost: '',
                                },
                              ],
                        )
                        setSearch('')
                      }}
                    >
                      <span className="font-medium">{product.name}</span>{' '}
                      <span className="font-identifier text-xs text-foreground-muted">
                        {product.sku}
                      </span>
                    </button>
                  </li>
                ))}
                {matches.length === 0 ? (
                  <li className="px-3 py-2 text-sm text-foreground-muted">No matches</li>
                ) : null}
              </ul>
            ) : null}
          </div>

          {lines.length > 0 ? (
            <ul className="divide-y divide-border rounded-md border border-border">
              {lines.map((line) => (
                <li
                  key={line.key}
                  className={`flex items-center gap-2 px-3 py-2 ${line.productId === '' && !line.office ? 'bg-warning/10' : ''}`}
                >
                  <div className="min-w-0 flex-1">
                    {line.office ? (
                      <>
                        <Input
                          value={line.name}
                          placeholder="What was bought (e.g. copy paper)"
                          aria-label="Office supplies description"
                          onChange={(e) =>
                            setLines((current) =>
                              current.map((l) =>
                                l.key === line.key ? { ...l, name: e.target.value } : l,
                              ),
                            )
                          }
                        />
                        <p className="mt-0.5 text-xs text-foreground-muted">
                          Office supplies — goes to expenses, not stock
                        </p>
                      </>
                    ) : line.productId === '' ? (
                      <>
                        <p className="truncate text-sm font-medium">
                          {line.invoiceText ?? line.name}
                        </p>
                        <button
                          type="button"
                          className="text-xs text-warning underline"
                          onClick={() => {
                            setFixingKey(line.key)
                            setSearch(line.invoiceText ?? '')
                          }}
                        >
                          Not in catalogue — choose a product
                        </button>
                      </>
                    ) : (
                      <>
                        <p className="truncate text-sm font-medium">{line.name}</p>
                        <p className="font-identifier text-xs text-foreground-muted">
                          {line.sku}
                          {line.invoiceText && line.invoiceText !== line.name
                            ? ` · invoice: ${line.invoiceText}`
                            : ''}
                        </p>
                      </>
                    )}
                  </div>
                  <Input
                    type="number"
                    min={1}
                    value={line.qty}
                    aria-label={`Quantity of ${line.name}`}
                    onChange={(e) =>
                      setLines((current) =>
                        current.map((l) =>
                          l.key === line.key ? { ...l, qty: e.target.value } : l,
                        ),
                      )
                    }
                    className="w-20 text-end"
                  />
                  <Input
                    type="number"
                    min={0}
                    step="0.01"
                    value={line.cost}
                    placeholder="Unit cost"
                    aria-label={`Unit cost of ${line.name}`}
                    onChange={(e) =>
                      setLines((current) =>
                        current.map((l) =>
                          l.key === line.key ? { ...l, cost: e.target.value } : l,
                        ),
                      )
                    }
                    className="w-28 text-end"
                  />
                  {canExpense ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      aria-label={
                        line.office
                          ? `Make ${line.name || 'this line'} goods again`
                          : `Mark ${line.name} as office supplies`
                      }
                      onClick={() =>
                        setLines((current) =>
                          current.map((l) =>
                            l.key === line.key
                              ? { ...l, office: !l.office, invoiceText: l.invoiceText ?? l.name }
                              : l,
                          ),
                        )
                      }
                    >
                      {line.office ? 'Goods' : 'Office supplies'}
                    </Button>
                  ) : null}
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label={`Remove ${line.name}`}
                    onClick={() => setLines((current) => current.filter((l) => l.key !== line.key))}
                  >
                    <Trash2 />
                  </Button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-foreground-muted">Search above and add what was ordered.</p>
          )}
          {canExpense ? (
            <div className="flex flex-wrap items-center gap-2">
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() =>
                  setLines((current) => [
                    ...current,
                    {
                      key: crypto.randomUUID(),
                      productId: '',
                      name: '',
                      sku: '',
                      qty: '1',
                      cost: '',
                      office: true,
                    },
                  ])
                }
              >
                <Plus className="size-4" /> Add an office-supplies line
              </Button>
              <span className="text-xs text-foreground-muted">
                For items on the same invoice that are not for resale — they become an expense.
              </span>
            </div>
          ) : null}
          {hasOfficeLines ? (
            <Field label="Expense category" hint="Where the office supplies are recorded.">
              {(props) => (
                <Select value={expenseCategoryId || undefined} onValueChange={setExpenseCategoryId}>
                  <SelectTrigger {...props}>
                    <SelectValue placeholder="Choose a category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </Field>
          ) : null}

          {unmatched ? (
            <p className="text-xs text-warning">Choose a product for each highlighted line.</p>
          ) : null}
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Shipping" hint="Optional. Its own line on the order — not stock.">
              {(props) => (
                <Input
                  {...props}
                  type="number"
                  min={0}
                  step="0.01"
                  value={shipping}
                  onChange={(e) => setShipping(e.target.value)}
                  className="text-end"
                />
              )}
            </Field>
            <Field label="Tax" hint="Optional.">
              {(props) => (
                <Input
                  {...props}
                  type="number"
                  min={0}
                  step="0.01"
                  value={tax}
                  onChange={(e) => setTax(e.target.value)}
                  className="text-end"
                />
              )}
            </Field>
          </div>
          <div className="space-y-2 text-sm">
            <div className="flex items-center gap-2">
              <Switch
                id="new-allocate-shipping"
                checked={allocateShipping}
                onCheckedChange={setAllocateShipping}
              />
              <label htmlFor="new-allocate-shipping">Add shipping to item cost</label>
            </div>
            <div className="flex items-center gap-2">
              <Switch
                id="new-allocate-tax"
                checked={allocateTax}
                onCheckedChange={setAllocateTax}
              />
              <label htmlFor="new-allocate-tax">Add tax to item cost</label>
            </div>
          </div>
          <Field
            label="Payment"
            hint="Cost entries made on receiving start with this status. Choose Paid if you have already paid this order."
          >
            {(props) => (
              <Select
                value={paymentStatus}
                onValueChange={(v) => setPaymentStatus(v as VendorPaymentStatus)}
              >
                <SelectTrigger {...props}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {VENDOR_PAYMENT_STATUSES.map((s) => (
                    <SelectItem key={s} value={s}>
                      {humanizeStatus(s)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </Field>
          {owesOnPaid ? <UnpaidOrderWarning owedCents={orderTotalCents} /> : null}
          {lines.length > 0 ? (
            <p className="text-xs text-foreground-muted">
              Order total {formatMoney(orderTotalCents)} (items {formatMoney(linesTotalCents)} +
              shipping + tax)
              {officeLines.length > 0
                ? ` + office supplies ${formatMoney(officeTotalCents)} = ${formatMoney(
                    orderTotalCents + officeTotalCents,
                  )} on the invoice`
                : ''}
              {invoiceTotalCents !== null
                ? ` · invoice total ${formatMoney(invoiceTotalCents)}${
                    invoiceTotalCents !== orderTotalCents + officeTotalCents
                      ? ' — these differ, check for a discount or a missed line'
                      : ''
                  }`
                : ''}
            </p>
          ) : null}

          <Field label="Note">
            {(props) => (
              <Textarea
                {...props}
                rows={2}
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
            )}
          </Field>
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          {owesOnPaid && can('expenses:manage') ? (
            <Button
              variant="secondary"
              loading={create.isPending}
              onClick={() => create.mutate(true)}
            >
              Save and record payment
            </Button>
          ) : null}
          <Button
            variant="primary"
            disabled={!valid}
            loading={create.isPending}
            onClick={() => create.mutate(false)}
          >
            Save order
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
